import { Box, Input, Select, Stack, Checkbox, Text, Textarea } from "@chakra-ui/react";
import MultiSelect from "./MultiSelect/MultiSelect";
import React, { useState, useEffect, Fragment } from "react";
import { prettyPrintCodeName } from "utils/text-utils";
import { LabelText } from "./text/TextTypes";
import { Question } from "@phosphor-icons/react";
import { toDate } from "utils";
import { splitCSV } from "utils/text-utils";

/**
 * Warning: Because this edits item.prop directly, it may not trigger a re-render of other widgets using item.prop.
 * You may need to use onChange to trigger a re-render of other widgets.
 *
 * @typedef {Object} PropInputProps
 * @property {string} label - Optional label for the input.
 * @property {any} item - The item object containing the property to be edited.
 * @property {string} prop - The property of the item to be edited.
 * @property {"text" | "textarea" | "select" | "checkbox" | "radio" | "number" | undefined} [type] - The type of input.
 * @property {string[]} options - Options for select type input.
 * @property {boolean} grouped - Whether the options are grouped.
 * If true, the format is [{group: string, options: string[]}]
 * @property {string} help - Help text for the input.
 * @property {string} className - Additional class names for styling.
 * @property {function} onChange - Optional function called after the item value is set.
 * @property {number} rows - Number of rows for textarea type input.
 * @property {boolean} multiple - Whether the input is a multiple select.
 * @property {boolean} readOnly - Whether the input is read-only.
 * @property {string} reason - Why the input is read-only (e.g. "auto-calculated").
 */

/**
 * Edit a property/field in a json object
 * @param {PropInputProps} props
 * @returns
 */
export default function PropInput({
  label,
  item,
  prop,
  type,
  help,
  className,
  onChange,
  inline,
  disabled,
  reason,
  convertValue,
  ...rest
}) {
  // split rest into layout and other props // ??maybe make this a utility function for reuse?
  const LAYOUT_PROPS_REGEX = /^(m[trbl]?|p[trbl]?|flex.*|w|h|minW|maxW|minH|maxH)$/;
  const layout = Object.fromEntries(Object.entries(rest).filter(([key]) => LAYOUT_PROPS_REGEX.test(key)));
  // remove layout props from rest
  const rest_without_layout = Object.fromEntries(Object.entries(rest).filter(([key]) => !LAYOUT_PROPS_REGEX.test(key)));
  if (!item) item = {};
  const item_prop_value = item[prop];
  const [value, setValue] = useState(item_prop_value);
  useEffect(() => {
    setValue(item_prop_value);
  }, [prop, item, item_prop_value]);

  if (label === null || label === undefined) label = prettyPrintCodeName(prop);
  const _onChange = rest.readOnly
    ? null
    : (e) => {
		let value = e.target.value;
		if (convertValue) {
			value = convertValue(value);
		}
        item[prop] = value;
        setValue(value);
        if (onChange) onChange(e);
      };
  let inputValue = value === undefined || value === null ? "" : value; // keep react happy
  let InputComponent;
  if (type === "select") {
    InputComponent = InputSelect;
  } else if (type === "checkbox") {
    InputComponent = InputCheckbox;
  } else if (type === "date") {
    InputComponent = Input;
    // format date so the input can handle it
    if (value) inputValue = toDate(value).toISOString().split("T")[0];
  } else if (type === "textarea") {
    InputComponent = Textarea;
  } else if (type === "number") {
    InputComponent = Input;
	convertValue = (value) => {
		return parseFloat(value);
	};
  } else {
    InputComponent = Input;
  }

  if (inline) {
    return (
      <Stack direction="row" gap={1} className={className} {...layout}>
        <LabelText>{label}</LabelText>
        {help && <HelpText label={prop}>{help}</HelpText>}
        <InputComponent
          value={inputValue}
          onChange={_onChange}
          type={type}
          {...rest_without_layout}
          isDisabled={disabled}
          title={reason}
        />
      </Stack>
    );
  }
  return (
    <Box className={className} {...layout}>
      <Stack direction="row" gap={1}>
        <LabelText>{label}</LabelText>
        {help && <HelpText label={prop}>{help}</HelpText>}
      </Stack>
      <InputComponent
        value={inputValue}
        onChange={_onChange}
        type={type}
        {...rest_without_layout}
        isDisabled={disabled}
        title={reason}
      />
    </Box>
  );
}

/**
 * Can only hold true/false values.
 */
function InputCheckbox({ value, onChange, ...rest }) {
  const checkboxOnChange = (e) => {
    const value = e.target.checked;
    onChange({ target: { value } }); // send an ersatz event object
  };
  return <Checkbox isChecked={!!value} onChange={checkboxOnChange} {...rest} />;
}

/**
 *
 * @param {Object} props
 * @param {any} props.value - The value of the input.
 * @param {function} props.onChange - The function to call when the value changes.
 * @param {string[]|{label: string, value: string}[]|{group: string, options: object[]}[]} props.options - The options for the input.
 * @param {boolean} props.multiple - Whether the input is a multiple select.
 * @param {function} props.labeller - The function to call to label the options.
 * @param {boolean} props.grouped - Whether the options are grouped. Grouped `options` must be an array of {group: string, options: object[]}
 * @param {boolean} props.canInputOther - If "Other" is selected, allow the user to enter a custom value. This will be returned as "Other: <custom-value>"
 * @returns
 */
function InputSelect({
  value,
  onChange,
  options,
  multiple,
  labeller,
  grouped,
  isDisabled,
  readOnly,
  canBeUnset,
  canInputOther,
  ...rest
}) {
  if (!labeller) labeller = (v) => "" + v;
  // sniff grouped?? No it could lead to confusing behaviour. the caller can set it to true or false as needed
  const [customValue, setCustomValue] = useState(""); // for Other: <custom-value>
  const originalOptions = options; // for debugging
  /** standardise the options format to [{label, value}]  */
  const toStandardOption = (v) =>
    v?.label !== undefined && v?.value !== undefined ? v : { label: labeller(v), value: v };
  if (!Array.isArray(options)) {
    console.error("options is not an array?!", options);
  }
  // adjust options to label/value formats
  if (grouped) {
    let options2 = options.map((optGroup) => {
      let optGroupOptions = optGroup.options;
      if (typeof optGroup.options === "string") {
        optGroupOptions = splitCSV(optGroup.options);
      }
      return {
        group: optGroup.group,
        options: optGroupOptions?.map(toStandardOption) || [],
      };
    });
    options = options2;
  } else {
    options = options.map(toStandardOption);
    if (canBeUnset && !multiple) {
      // Note: the multi-select has (x) buttons to remove items, so we don't need a null option
      // add a value:null option, at the top so it is the default shown (unless there is already a null option)
      if (!options.find((o) => o.value === null || o.value === undefined || o.value === "")) {
        options = [{ label: "", value: null }, ...options];
      }
    }
  } // ./adjust options to label/value formats
  // multi-select?
  if (multiple) {
    // if not an array, make it one
    if (!Array.isArray(value)) {
      value = value === null || value === undefined || value === "" ? [] : [value];
    }
    // MultiSelect expects to work with [{label, value}], so convert to/from that format
    const selectedOptions = (value || []).map(toStandardOption);
    if (grouped) {
      // HACK: without upgrading our UX libs, we need to handle grouped options manually as multiple inputs
      const multiGroupedOnChange = (newOptions, optGroup) => {
        console.log("multiGroupedOnChange", value, newOptions, optGroup);
        let newValue = value.filter((v) => !optGroup.options?.map((opt) => opt.value).includes(v)); // values from other groups
        const newOptionValues = newOptions.map((multiOption) => multiOption.value);
        newValue = newValue.concat(newOptionValues);
        onChange({ target: { value: newValue } });
      };
      if (readOnly) {
        // fixes bug seen July 2025: readOnly looks like a confusing block of text
        return selectedOptions.map((o) => <Text key={o.value}>{o.label}</Text>);
      }
      return (
        <>
          {options.map((optGroup) => (
            <Fragment key={optGroup.group}>
              <Text>{optGroup.group}</Text>
              <MultiSelect
                isMulti={true}
                value={selectedOptions.filter((o) => optGroup.options?.map((opt) => opt.value).includes(o.value))}
                onChange={(newOptions) => multiGroupedOnChange(newOptions, optGroup)}
                options={optGroup.options || []}
                isDisabled={isDisabled}
                isReadOnly={readOnly}
                {...rest}
              />
            </Fragment>
          ))}
        </>
      );
    } // ./multiple+grouped
    // convert options to [{label, value}] format if not already in that format
    let multiOptions = options.map((v) =>
      v?.label !== undefined && v?.value !== undefined ? v : { label: labeller(v), value: v }
    );
    const multiOnChange = (newOptions) => {
      onChange({ target: { value: newOptions.map((multiOption) => multiOption.value) } });
    };
    return (
      <MultiSelect
        options={multiOptions}
        isMulti={true}
        value={selectedOptions}
        onChange={multiOnChange}
        isDisabled={isDisabled}
        isReadOnly={readOnly}
        {...rest}
      />
    );
  } // ./multiple
  if (readOnly) {
    // HACK: Chakra-ui Select doesn't support readOnly, so fake it with a single option
    options = [{ label: labeller(value), value: value }];
    grouped = false; // switch off grouping for readOnly just-display-the-selected-option
  }
  // Other?
  let onChangeCustomValue = (e) => {
    setCustomValue(e.target.value);
    const otherValue = e.target.value ? "Other: " + e.target.value : null;
    onChange({ target: { value: otherValue } });
  };
  let selectValue = value;
  if (canInputOther && isOther(value)) {
    // for other - pick the select option that triggers the custom value input
    selectValue = options.map((o) => o.value).find((o) => isOther(o));
  }

  return (
    <>
      <Select
        type="select"
        value={selectValue}
        onChange={onChange}
        isDisabled={isDisabled}
        isReadOnly={readOnly}
        {...rest}
      >
        {grouped &&
          options.map((optionGroup) => (
            <optgroup key={optionGroup.group} label={optionGroup.group}>
              {optionGroup.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        {!grouped &&
          options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
      </Select>
      {canInputOther &&
        isOther(value) /* matches "Other: <custom-value>" and options like eg "Other please specify" */ && (
          <Input
            mt={1}
            type="text"
            value={customValue}
            onChange={onChangeCustomValue}
            isDisabled={isDisabled}
            isReadOnly={readOnly}
            placeholder="Please specify"
          />
        )}
    </>
  );
}

/**
 * HACK: Does this option indicate "Other, please specify" or similar? Also matches values of the form "Other: <custom-value>"
 */
export function isOther(value) {
  return typeof value === "string" && value.toLowerCase().startsWith("other");
}

function HelpText({ children }) {
  if (!children) return null;
  let tooltip;
  if (Array.isArray(children)) {
    tooltip = children.join("\n");
  } else {
    tooltip = "" + children;
  }
  return (
    <Box title={tooltip}>
      <Question />
    </Box>
  );
}
